import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import authFetch from '../utils/authFetch';
import { normalizeScoreBreakdown } from '../utils/scoreBreakdown';
import { 
    DocumentTextIcon, 
    MagnifyingGlassIcon,
    FunnelIcon,
    StarIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    CalendarIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    LightBulbIcon
} from '@heroicons/react/24/outline';

// Composant pour afficher les suggestions d'amélioration de manière structurée
const FeedbackDisplay = ({ feedback }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [parsedFeedback, setParsedFeedback] = useState(null);

    useEffect(() => {
        if (feedback) {
            try {
                // Essayer de parser le JSON
                const parsed = JSON.parse(feedback);
                setParsedFeedback(parsed);
            } catch (error) {
                // Si ce n'est pas du JSON valide, traiter comme du texte simple
                setParsedFeedback({ text: feedback });
            }
        }
    }, [feedback]);

    if (!feedback) return null;

    const renderFeedbackContent = () => {
        if (!parsedFeedback) {
            return (
                <div className="text-gray-700 text-sm">
                    {feedback.length > 200 && !isExpanded 
                        ? `${feedback.substring(0, 200)}...` 
                        : feedback
                    }
                </div>
            );
        }

        // Nouveau format JSON (scoreBreakdown + pointsForts + suggestions + mots-clés)
        const hasNewFormat = parsedFeedback.suggestions && Array.isArray(parsedFeedback.suggestions) && (parsedFeedback.pointsForts?.length > 0 || parsedFeedback.scoreBreakdown);
        if (hasNewFormat) {
            const pointsFortsList = parsedFeedback.pointsForts || [];
            const suggestionsList = parsedFeedback.suggestions || [];
            const breakdown = normalizeScoreBreakdown(parsedFeedback.scoreBreakdown, parsedFeedback.score);
            const presents = parsedFeedback.motsClesPresents || [];
            const manquants = parsedFeedback.motsClesManquants || [];
            return (
                <div className="space-y-4">
                    {breakdown && typeof breakdown === 'object' && (
                        <div className="text-xs text-gray-500 break-words">
                            Détail : Structure {breakdown.structure ?? '-'} · Clarté {breakdown.clarte ?? '-'} · Mots-clés {breakdown.motsCles ?? '-'} · Secteur {breakdown.secteur ?? '-'} · Chiffres {breakdown.quantification ?? '-'}
                        </div>
                    )}
                    {pointsFortsList.length > 0 && (
                        <div>
                            <h5 className="font-semibold text-green-700 mb-1 flex items-center gap-1 text-sm">
                                <CheckCircleIcon className="w-4 h-4" /> Points forts
                            </h5>
                            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5 break-words">
                                {pointsFortsList.slice(0, isExpanded ? undefined : 3).map((p, i) => <li key={i}>{p}</li>)}
                            </ul>
                        </div>
                    )}
                    {(presents.length > 0 || manquants.length > 0) && (
                        <div className="flex flex-wrap gap-2 text-xs">
                            {presents.length > 0 && (
                                <span className="text-gray-600">
                                    Mots-clés présents : {presents.slice(0, 5).join(', ')}{presents.length > 5 ? '…' : ''}
                                </span>
                            )}
                            {manquants.length > 0 && (
                                <span className="text-amber-700">
                                    À ajouter : {manquants.slice(0, 5).join(', ')}{manquants.length > 5 ? '…' : ''}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        {suggestionsList.slice(0, isExpanded ? undefined : 5).map((s, i) => {
                            const title = s.titre ?? s.title;
                            const desc = s.description;
                            return (
                                <div key={i} className="bg-white rounded-lg p-2 border-l-4 border-blue-500">
                                    {title && <h5 className="font-semibold text-gray-800 text-sm mb-0.5 break-words">{title}</h5>}
                                    {desc && <p className="text-sm text-gray-700 break-words">{desc}</p>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }

        // Si c'est un objet avec des suggestions structurées (titre/description ou title/description)
        if (parsedFeedback.suggestions && Array.isArray(parsedFeedback.suggestions)) {
            return (
                <div className="space-y-3">
                    {parsedFeedback.suggestions.slice(0, isExpanded ? undefined : 5).map((suggestion, index) => {
                        const title = suggestion.titre ?? suggestion.title;
                        const desc = suggestion.description;
                        return (
                            <div key={index} className="bg-white rounded-lg p-3 border-l-4 border-blue-500">
                                <div className="flex items-start gap-2">
                                    <LightBulbIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        {title && <h5 className="font-semibold text-gray-800 mb-1 break-words">{title}</h5>}
                                        {desc && <p className="text-sm text-gray-700 break-words">{desc}</p>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Si c'est un objet avec des points d'amélioration
        if (parsedFeedback.improvements && Array.isArray(parsedFeedback.improvements)) {
            return (
                <div className="space-y-2">
                    {parsedFeedback.improvements.slice(0, isExpanded ? undefined : 3).map((improvement, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-gray-700">{improvement}</p>
                        </div>
                    ))}
                </div>
            );
        }

        // Si c'est un objet avec des forces (strengths ou pointsForts)
        const strengthsList = parsedFeedback.strengths ?? parsedFeedback.pointsForts;
        if (strengthsList && Array.isArray(strengthsList)) {
            return (
                <div className="space-y-2">
                    <div className="mb-3">
                        <h5 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                            <CheckCircleIcon className="w-4 h-4" />
                            Points forts
                        </h5>
                        {strengthsList.slice(0, isExpanded ? undefined : 5).map((strength, index) => (
                            <div key={index} className="flex items-start gap-2 ml-6">
                                <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                <p className="text-sm text-gray-700">{strength}</p>
                            </div>
                        ))}
                    </div>
                    {parsedFeedback.improvements && Array.isArray(parsedFeedback.improvements) && (
                        <div>
                            <h5 className="font-semibold text-orange-700 mb-2 flex items-center gap-2">
                                <ExclamationTriangleIcon className="w-4 h-4" />
                                Améliorations suggérées
                            </h5>
                            {parsedFeedback.improvements.slice(0, isExpanded ? undefined : 2).map((improvement, index) => (
                                <div key={index} className="flex items-start gap-2 ml-6">
                                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <p className="text-sm text-gray-700">{improvement}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // Fallback pour du texte simple
        return (
            <div className="text-gray-700 text-sm leading-relaxed">
                {parsedFeedback.text && (
                    <div className="whitespace-pre-wrap">
                        {parsedFeedback.text.length > 300 && !isExpanded 
                            ? `${parsedFeedback.text.substring(0, 300)}...` 
                            : parsedFeedback.text
                        }
                    </div>
                )}
            </div>
        );
    };

    const hasMoreContent = () => {
        if (!parsedFeedback) {
            return feedback.length > 200;
        }
        const pointsFortsList = parsedFeedback.pointsForts || [];
        if (parsedFeedback.suggestions && Array.isArray(parsedFeedback.suggestions)) {
            return parsedFeedback.suggestions.length > 5 || pointsFortsList.length > 3;
        }
        if (parsedFeedback.suggestions) {
            return parsedFeedback.suggestions.length > 2;
        }
        
        if (parsedFeedback.improvements) {
            return parsedFeedback.improvements.length > 3;
        }
        
        if (parsedFeedback.text) {
            return parsedFeedback.text.length > 300;
        }
        
        return false;
    };

    return (
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-100 min-w-0 overflow-hidden">
            <div className="flex items-start justify-between gap-2 mb-3 min-w-0">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2 min-w-0 text-sm sm:text-base">
                    <LightBulbIcon className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="break-words">Suggestions d&apos;amélioration</span>
                </h4>
                {hasMoreContent() && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-blue-600 text-sm font-medium flex items-center gap-1 shrink-0"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUpIcon className="w-4 h-4" />
                                Réduire
                            </>
                        ) : (
                            <>
                                <ChevronDownIcon className="w-4 h-4" />
                                Voir plus
                            </>
                        )}
                    </button>
                )}
            </div>
            
            {renderFeedbackContent()}
            
            {hasMoreContent() && !isExpanded && (
                <div className="mt-3 text-xs text-gray-500">
                    Cliquez sur "Voir plus" pour afficher toutes les suggestions
                </div>
            )}
        </div>
    );
};

const AnalysisHistory = () => {
    const [analyses, setAnalyses] = useState([]);
    const [filteredAnalyses, setFilteredAnalyses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSector, setSelectedSector] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [sortBy, setSortBy] = useState('date');
    const [error, setError] = useState('');

    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user.id);

    const sectors = [
        { value: '', label: 'Tous les secteurs' },
        { value: 'informatique', label: 'Informatique' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'finance', label: 'Finance' },
        { value: 'ressources_humaines', label: 'Ressources Humaines' },
        { value: 'santé', label: 'Santé' },
        { value: 'education', label: 'Éducation' },
        { value: 'logistique', label: 'Logistique' },
        { value: 'commerce', label: 'Commerce' },
        { value: 'ingenierie', label: 'Ingénierie' },
        { value: 'administration', label: 'Administration' },
        { value: 'juridique', label: 'Juridique' },
        { value: 'communication', label: 'Communication' },
        { value: 'construction', label: 'Construction' },
        { value: 'immobilier', label: 'Immobilier' },
        { value: 'agriculture', label: 'Agriculture' },
        { value: 'transports', label: 'Transports' },
        { value: 'tourisme', label: 'Tourisme' },
        { value: 'arts', label: 'Arts' },
        { value: 'recherche', label: 'Recherche' },
        { value: 'industrie', label: 'Industrie' },
        { value: 'environnement', label: 'Environnement' },
        { value: 'energie', label: 'Énergie' },
        { value: 'services_publics', label: 'Services Publics' },
        { value: 'service_client', label: 'Service Client' },
        { value: 'sport', label: 'Sport' },
        { value: 'mode', label: 'Mode' },
        { value: 'alimentation', label: 'Alimentation' },
        { value: 'bancaire', label: 'Bancaire' },
        { value: 'assurances', label: 'Assurances' },
        { value: 'biotechnologies', label: 'Biotechnologies' },
        { value: 'pharmaceutique', label: 'Pharmaceutique' },
        { value: 'chimie', label: 'Chimie' },
        { value: 'médias', label: 'Médias' },
        { value: 'audiovisuel', label: 'Audiovisuel' },
        { value: 'édition', label: 'Édition' },
        { value: 'cosmétique', label: 'Cosmétique' },
        { value: 'automobile', label: 'Automobile' },
        { value: 'aéronautique', label: 'Aéronautique' },
        { value: 'spatial', label: 'Spatial' },
        { value: 'defense', label: 'Défense' },
        { value: 'telecommunications', label: 'Télécommunications' },
        { value: 'nouvelles_technologies', label: 'Nouvelles Technologies' },
        { value: 'cybersecurite', label: 'Cybersécurité' },
        { value: 'ecommerce', label: 'E-commerce' },
        { value: 'distribution', label: 'Distribution' },
        { value: 'evenementiel', label: 'Événementiel' },
        { value: 'formation', label: 'Formation' },
        { value: 'restauration', label: 'Restauration' },
        { value: 'hôtellerie', label: 'Hôtellerie' },
        { value: 'recyclage', label: 'Recyclage' },
        { value: 'gestion_de_projet', label: 'Gestion de Projet' },
        { value: 'consulting', label: 'Consulting' },
        { value: 'artisanat', label: 'Artisanat' },
        { value: 'mines', label: 'Mines' },
        { value: 'metallurgie', label: 'Métallurgie' },
        { value: 'energie_renouvelable', label: 'Énergies Renouvelables' },
        { value: 'robotique', label: 'Robotique' },
        { value: 'intelligence_artificielle', label: 'Intelligence Artificielle' }
    ];

    const periods = [
        { value: 'all', label: 'Toute la période' },
        { value: 'today', label: 'Aujourd\'hui' },
        { value: 'week', label: 'Cette semaine' },
        { value: 'month', label: 'Ce mois' },
        { value: 'year', label: 'Cette année' }
    ];

    const sortOptions = [
        { value: 'date', label: 'Date (plus récent)' },
        { value: 'date_old', label: 'Date (plus ancien)' },
        { value: 'score', label: 'Score (plus élevé)' },
        { value: 'score_low', label: 'Score (plus faible)' },
        { value: 'sector', label: 'Secteur' }
    ];

    useEffect(() => {
        fetchAnalysisHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        filterAndSortAnalyses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyses, searchTerm, selectedSector, selectedPeriod, sortBy]);

    const fetchAnalysisHistory = async () => {
        try {
            console.log('🔍 Récupération de l\'historique pour userId:', userId);
            console.log('🔑 Token:', token ? 'Présent' : 'Absent');
            
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/history/${userId}`);

            console.log('📡 Réponse du serveur:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Erreur serveur:', errorText);
                throw new Error(`Erreur ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            console.log('📊 Données reçues:', data);
            console.log('📋 Nombre d\'analyses:', data.analyses?.length || 0);
            
            setAnalyses(data.analyses || []);
        } catch (error) {
            console.error('💥 Erreur complète:', error);
            setError(`Impossible de charger l'historique: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const filterAndSortAnalyses = () => {
        let filtered = [...analyses];

        // Filtre par recherche
        if (searchTerm) {
            filtered = filtered.filter(analysis => 
                analysis.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                analysis.sector?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                analysis.feedback?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtre par secteur
        if (selectedSector) {
            filtered = filtered.filter(analysis => analysis.sector === selectedSector);
        }

        // Filtre par période
        const now = new Date();
        switch (selectedPeriod) {
            case 'today':
                filtered = filtered.filter(analysis => {
                    const analysisDate = new Date(analysis.createdAt);
                    return analysisDate.toDateString() === now.toDateString();
                });
                break;
            case 'week':
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter(analysis => new Date(analysis.createdAt) >= weekAgo);
                break;
            case 'month':
                const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter(analysis => new Date(analysis.createdAt) >= monthAgo);
                break;
            case 'year':
                const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                filtered = filtered.filter(analysis => new Date(analysis.createdAt) >= yearAgo);
                break;
            default:
                // Pas de filtre de période
                break;
        }

        // Tri
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date':
                    return new Date(b.createdAt) - new Date(a.createdAt);
                case 'date_old':
                    return new Date(a.createdAt) - new Date(b.createdAt);
                case 'score':
                    return (b.score || 0) - (a.score || 0);
                case 'score_low':
                    return (a.score || 0) - (b.score || 0);
                case 'sector':
                    return (a.sector || '').localeCompare(b.sector || '');
                default:
                    return 0;
            }
        });

        setFilteredAnalyses(filtered);
    };

    const deleteAnalysis = async (analysisId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette analyse ?')) {
            return;
        }
    
        try {
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/analysis/${analysisId}`, {
                method: 'DELETE',
                headers: { 'X-User-Id': userId },
            });
    
            if (!response.ok) {
                throw new Error('Erreur lors de la suppression');
            }
    
            // Mettre à jour la liste locale
            setAnalyses(analyses.filter(analysis => analysis.id !== analysisId));
        } catch (error) {
            setError('Impossible de supprimer l\'analyse');
            console.error('Erreur:', error);
        }
    };

    const exportAnalysis = (analysis) => {
        // Parser le feedback pour un affichage plus lisible
        let parsedFeedback = analysis.feedback;
        try {
            const parsed = JSON.parse(analysis.feedback);
            if (parsed.suggestions && Array.isArray(parsed.suggestions)) {
                parsedFeedback = parsed.suggestions.map((s, i) => 
                    `${i + 1}. ${s.titre || s.title || 'Suggestion'}: ${s.description || ''}`
                ).join('\n\n');
            } else if (parsed.improvements) {
                parsedFeedback = parsed.improvements.map((imp, i) => 
                    `${i + 1}. ${imp}`
                ).join('\n\n');
            } else if (parsed.text) {
                parsedFeedback = parsed.text;
            }
        } catch (error) {
            // Garder le feedback original si le parsing échoue
        }

        // Créer un rapport lisible
        const report = `RAPPORT D'ANALYSE CV
========================

Fichier analysé: ${analysis.filename}
Date d'analyse: ${new Date(analysis.createdAt).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
})}
Secteur cible: ${analysis.sector}
Score obtenu: ${analysis.score || 'Non disponible'}/100

${analysis.score ? `
ÉVALUATION DU SCORE
------------------
${analysis.score >= 80 ? 'Excellent ! Votre CV est très bien structuré.' :
  analysis.score >= 60 ? 'Bon travail ! Quelques améliorations possibles.' :
  analysis.score >= 40 ? 'Correct, mais des améliorations importantes sont nécessaires.' :
  'Votre CV nécessite une refonte complète.'}
` : ''}

SUGGESTIONS D'AMÉLIORATION
=========================
${parsedFeedback}

---
Généré par CV Analyse
https://www.cv-analyse.fr
`;

        const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapport-analyse-${analysis.filename.replace(/\.pdf$/i, '')}-${new Date(analysis.createdAt).toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-700 bg-green-100 border-green-200';
        if (score >= 60) return 'text-yellow-700 bg-yellow-100 border-yellow-200';
        return 'text-red-700 bg-red-100 border-red-200';
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return 'Excellent';
        if (score >= 60) return 'Bon';
        if (score >= 40) return 'Correct';
        return 'À améliorer';
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="page-shell flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement de l'historique...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell overflow-x-hidden">
            <div className="page-wrap">
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Historique des analyses</h1>
                        <p className="page-subtitle">Retrouvez vos analyses et leurs résultats</p>
                    </div>
                    <Link to="/upload" className="btn-primary shrink-0">
                        Analyser un CV
                    </Link>
                </div>

                <div className="stat-strip">
                    <div className="stat-cell">
                        <div className="text-xs text-slate-500 mb-0.5">Analyses</div>
                        <div className="text-xl font-semibold text-slate-900">{analyses.length}</div>
                    </div>
                    <div className="stat-cell">
                        <div className="text-xs text-slate-500 mb-0.5">Score moyen</div>
                        <div className="text-xl font-semibold text-slate-900">
                            {analyses.length > 0 ? Math.round(analyses.reduce((acc, a) => acc + (a.score || 0), 0) / analyses.length) : 0}
                        </div>
                    </div>
                    <div className="stat-cell">
                        <div className="text-xs text-slate-500 mb-0.5">Secteurs</div>
                        <div className="text-xl font-semibold text-slate-900">
                            {new Set(analyses.map(a => a.sector)).size}
                        </div>
                    </div>
                    <div className="stat-cell">
                        <div className="text-xs text-slate-500 mb-0.5">Ce mois</div>
                        <div className="text-xl font-semibold text-slate-900">
                            {analyses.filter(a => new Date(a.createdAt) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length}
                        </div>
                    </div>
                </div>

                <div className="toolbar">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 min-w-0">
                        {/* Search */}
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Sector Filter */}
                        <select
                            value={selectedSector}
                            onChange={(e) => setSelectedSector(e.target.value)}
                            className="w-full min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {sectors.map((sector) => (
                                <option key={sector.value} value={sector.value}>
                                    {sector.label}
                                </option>
                            ))}
                        </select>

                        {/* Period Filter */}
                        <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="w-full min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {periods.map((period) => (
                                <option key={period.value} value={period.value}>
                                    {period.label}
                                </option>
                            ))}
                        </select>

                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {sortOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Results */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-600">{error}</p>
                    </div>
                )}

                {filteredAnalyses.length === 0 ? (
                    <div className="card-surface p-10 text-center">
                        <DocumentTextIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">Aucune analyse trouvée</h3>
                        <p className="text-gray-500 mb-6">
                            {analyses.length === 0 
                                ? "Vous n'avez pas encore effectué d'analyse de CV."
                                : "Aucune analyse ne correspond à vos critères de recherche."
                            }
                        </p>
                        {analyses.length === 0 && (
                            <Link
                                to="/upload"
                                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl"
                            >
                                Analyser mon CV
                            </Link>
                        )}
                        
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {filteredAnalyses.map((analysis) => (
                            <div key={analysis.id} className="card-surface p-4 sm:p-6 min-w-0 overflow-hidden">
                                <div className="flex flex-col gap-4 min-w-0">
                                    <div className="min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 min-w-0">
                                            <div className="min-w-0">
                                                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 break-all">
                                                    {analysis.filename || 'CV analysé'}
                                                </h3>
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                                                    <div className="flex items-center min-w-0">
                                                        <CalendarIcon className="w-4 h-4 mr-1 shrink-0" />
                                                        <span className="break-words">{formatDate(analysis.createdAt)}</span>
                                                    </div>
                                                    <div className="flex items-center min-w-0">
                                                        <FunnelIcon className="w-4 h-4 mr-1 shrink-0" />
                                                        <span className="break-words">{analysis.sector}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {analysis.score !== null && (
                                                <div className={`self-start px-3 py-2 rounded-lg text-sm font-semibold border shrink-0 ${getScoreColor(analysis.score)}`}>
                                                    <div className="flex items-center gap-1">
                                                        <StarIcon className="w-4 h-4" />
                                                        <span>{analysis.score}/100</span>
                                                    </div>
                                                    <div className="text-xs mt-0.5 opacity-75">
                                                        {getScoreLabel(analysis.score)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {analysis.feedback && (
                                            <FeedbackDisplay feedback={analysis.feedback} />
                                        )}
                                        {analysis.isLimited && (
                                            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                                                <p className="text-sm text-gray-700 mb-3">
                                                    Aperçu uniquement. Le rapport complet (détail, toutes les recommandations, ATS et CV restructuré) se débloque dès le paiement.
                                                </p>
                                                <Link
                                                    to="/subscription"
                                                    className="inline-block bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                                                >
                                                    Débloquer le rapport complet
                                                </Link>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => exportAnalysis(analysis)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Exporter l'analyse"
                                        >
                                            <ArrowDownTrayIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => deleteAnalysis(analysis.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Supprimer l'analyse"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalysisHistory; 